import {
  Box,
  Column,
  Columns,
  ContentBlock,
  Divider,
  Heading,
  List,
  Stack,
  Text,
} from 'braid-design-system';

import { ClientOnly } from 'src/components/ClientOnly';
import { TextLink } from 'src/components/Link/Link';
import { Nav } from 'src/components/Nav/Nav';
import { PageWrapper } from 'src/components/PageWrapper/PageWrapper';
import { TRACKING_CODES } from 'src/constants';
import { links } from 'src/links';
import type { PageProps } from 'src/types';
import { useConfig } from 'src/utils/hooks/useContext';
import { slugify } from 'src/utils/slugify';

import { EnquiryForm } from './components/EnquiryForm/EnquiryForm';
import { SideInfo } from './components/SideInfo/SideInfo';

export const IntegrationRequest = ({ slug }: PageProps) => {
  const { environment } = useConfig();
  const enabledDate = new Date('2025-01-01');
  const integrationsClosed =
    new Date() < enabledDate && environment === 'production';

  const pageTrackingProps = {
    page: `${slugify({ str: slug })}`,
    eventName: `${slugify({ str: slug })}-viewed`,
    siteSection: 'partners',
  };

  return (
    <PageWrapper pageTrackingProps={pageTrackingProps} title={slug}>
      <Nav activeTab="integration-request" />
      <Box paddingY="xlarge" paddingX={['gutter', 'gutter', 'none']}>
        <ContentBlock>
          <Stack space="xlarge">
            <Heading level="1">Integration Request for Partners</Heading>

            <Text>
              If you are a SEEK customer wanting to connect your SEEK account
              with one of our existing{' '}
              <TextLink
                href="/connected-partners/"
                tracking={TRACKING_CODES.connected_partners_link_clicked}
              >
                Connected Partners
              </TextLink>{' '}
              please do not fill in this form. We invite you to fill out our{' '}
              <TextLink
                href={links.LINK_YOUR_SOFTWARE.path}
                tracking={links.LINK_YOUR_SOFTWARE.tracking}
              >
                dedicated form
              </TextLink>{' '}
              to link your software with SEEK.
            </Text>
            <ClientOnly>
              {integrationsClosed ? (
                <>
                  <Divider />
                  <Stack space="large">
                    <Text>
                      Please be advised that our integration request channel is
                      now closed for the remainder of 2024. We will begin
                      reviewing and approving new requests in the new year.
                    </Text>
                    <Text>
                      If you wish to be considered for integration in 2025,
                      kindly submit your integration request form in January.
                    </Text>
                    <Text>
                      Thank you for your understanding, and we look forward to
                      reconnecting in the new year.
                    </Text>
                    <Text>Best regards, </Text>
                    <Text>SEEK Partnerships Team </Text>
                  </Stack>
                </>
              ) : (
                <>
                  <SideInfo />
                  <Stack space="medium">
                    <Columns space="xlarge" collapseBelow="tablet">
                      <Column>
                        <EnquiryForm />
                      </Column>
                      <Column width="2/5">
                        <Stack space="medium">
                          <Text weight="strong">
                            Not wanting to build an integration but still have
                            an enquiry?
                          </Text>
                          <List>
                            <Text>
                              Job seekers contact us{' '}
                              <TextLink
                                href="/contact-us/"
                                tracking={
                                  TRACKING_CODES.contact_us_link_clicked
                                }
                                product="candidate"
                              >
                                here
                              </TextLink>
                            </Text>
                            <Text>
                              Employers contact us{' '}
                              <TextLink
                                href="/contactus/"
                                tracking={
                                  TRACKING_CODES.talent_contact_us_link_clicked
                                }
                              >
                                here
                              </TextLink>
                            </Text>
                          </List>
                          <Text>
                            Or, search for answers in our{' '}
                            <TextLink
                              href="https://help.seek.com.au/employer/s/"
                              tracking={
                                TRACKING_CODES.talent_help_center_link_clicked
                              }
                              skipResolvedUrl={true}
                            >
                              Help center
                            </TextLink>
                            .
                          </Text>
                        </Stack>
                      </Column>
                    </Columns>
                  </Stack>
                </>
              )}
            </ClientOnly>
          </Stack>
        </ContentBlock>
      </Box>
    </PageWrapper>
  );
};
